import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, Plus, Trash2, Save, DollarSign, Sparkles, UserCheck,
  ChevronDown, ChevronRight, Lock, SlidersHorizontal, ShieldAlert, BarChart3,
  Check, X,
} from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminAvatarStudio() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-avatar-studio">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => navigate('/admin/ai-talent')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to AI Talent
        </button>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-1">
          <Sparkles className="w-6 h-6 text-violet-600" /> Custom Avatar Studio
        </h1>
        <p className="text-sm text-slate-500 mb-6">Set Build-Your-Own-Avatar creation pricing and review incoming custom avatar builds. Changes apply to the website instantly.</p>
        <Tabs defaultValue="pricing">
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="pricing" data-testid="tab-pricing"><DollarSign className="w-4 h-4 mr-1" /> Creation Pricing</TabsTrigger>
            <TabsTrigger value="queue" data-testid="tab-queue"><UserCheck className="w-4 h-4 mr-1" /> Build Queue</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-studio-config"><SlidersHorizontal className="w-4 h-4 mr-1" /> Generation Config</TabsTrigger>
            <TabsTrigger value="moderation" data-testid="tab-moderation"><ShieldAlert className="w-4 h-4 mr-1" /> Moderation</TabsTrigger>
            <TabsTrigger value="usage" data-testid="tab-usage"><BarChart3 className="w-4 h-4 mr-1" /> Credits &amp; Usage</TabsTrigger>
          </TabsList>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="queue"><QueueTab /></TabsContent>
          <TabsContent value="config"><StudioConfigTab /></TabsContent>
          <TabsContent value="moderation"><ModerationTab /></TabsContent>
          <TabsContent value="usage"><UsageTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const blankPkg = { code: '', name: '', description: '', price: 0, currency: 'usd', includes: [], is_default: false, is_active: true, display_order: 0 };

function PricingTab() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/custom-avatar-pricing'); setPackages(r.data.packages); }
    catch { toast.error('Could not load pricing'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft, price: Number(draft.price), display_order: Number(draft.display_order) || 0,
        includes: typeof draft.includes === 'string' ? draft.includes.split('\n').filter(Boolean) : (draft.includes || []) };
      if (draft.id) await api.patch(`/admin/custom-avatar-pricing/${draft.id}`, payload);
      else await api.post('/admin/custom-avatar-pricing', payload);
      toast.success('Saved'); setDraft(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this package?')) return;
    try { await api.delete(`/admin/custom-avatar-pricing/${id}`); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDraft({ ...blankPkg })} data-testid="add-package-btn"><Plus className="w-4 h-4 mr-1" /> New package</Button>
      </div>
      {packages.map((p) => (
        <Card key={p.id} data-testid="pricing-row">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold flex items-center gap-2">{p.name} {p.is_default && <Badge className="bg-violet-100 text-violet-700">Default</Badge>} {!p.is_active && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}</div>
              <div className="text-xs text-slate-500">{p.code} · {p.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-emerald-700">{money(p.price)}</span>
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...p, includes: (p.includes || []).join('\n') })} data-testid="edit-package-btn">Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {draft && (
        <Card className="border-violet-300" data-testid="package-editor">
          <CardContent className="p-4 space-y-3">
            <div className="font-semibold">{draft.id ? 'Edit' : 'New'} creation package</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <L label="Code"><Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} data-testid="pkg-code" /></L>
              <L label="Name"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="pkg-name" /></L>
              <L label="Price (USD)"><Input type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="pkg-price" /></L>
              <L label="Display order"><Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: e.target.value })} /></L>
            </div>
            <L label="Description"><Input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></L>
            <L label="Includes (one per line)"><textarea className="w-full border rounded-md p-2 text-sm" rows={3} value={draft.includes} onChange={(e) => setDraft({ ...draft, includes: e.target.value })} /></L>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} /> Default</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} data-testid="save-package-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function QueueTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/custom-avatars/builds'); setData(r.data); }
    catch { toast.error('Could not load builds'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/custom-avatars/builds/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>;
  const builds = data?.builds || [];

  return (
    <div className="space-y-3">
      {!builds.length && <Card><CardContent className="p-10 text-center text-slate-400">No custom avatar builds yet.</CardContent></Card>}
      {builds.map((b) => (
        <Card key={b.request_id} data-testid="build-row">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [b.request_id]: !o[b.request_id] }))}>
              <div className="flex items-center gap-2">
                {open[b.request_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <div>
                  <div className="font-semibold">{b.name}</div>
                  <div className="text-xs text-slate-500">{b.client_email || 'guest'} · {b.source}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={b.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{b.payment_status}</Badge>
                <Badge className="bg-slate-100 text-slate-700">{b.status}</Badge>
                {b.creation_price != null && <span className="text-sm font-semibold text-emerald-700">{money(b.creation_price)}</span>}
              </div>
            </div>
            {open[b.request_id] && (
              <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><Lock className="w-3 h-3" /> Locked identity</div>
                  <div className="text-sm space-y-0.5">
                    {Object.entries(b.locked_identity || b.builder_spec || {}).map(([k, v]) => (
                      <div key={k}><span className="text-slate-400">{k}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}</div>
                    ))}
                    {!b.locked_identity && !b.builder_spec && <span className="text-slate-400">No spec</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1">Reference images ({(b.reference_images || []).length})</div>
                  <div className="flex flex-wrap gap-2">
                    {(b.reference_images || []).map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noreferrer" className="text-xs text-violet-600 underline">{(r.aspects || []).join('/') || 'ref'} #{i + 1}</a>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {['in_production', 'ready_for_review', 'completed', 'declined'].map((s) => (
                      <Button key={s} size="sm" variant="outline" onClick={() => setStatus(b.request_id, s)} data-testid={`set-${s}`}>{s.replace(/_/g, ' ')}</Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block"><span className="text-xs text-slate-500">{label}</span>{children}</label>
);


const CFG_FIELDS = [
  { key: 'monthly_allowance', label: 'Included generations / month' },
  { key: 'credits_per_pack', label: 'Credits per pack' },
  { key: 'credit_price_cents', label: 'Price per credit (cents)' },
  { key: 'est_cost_cents_per_gen', label: 'Est. provider cost / gen (cents)' },
];

function StudioConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/avatar-studio/admin/config'); setCfg(r.data.effective); }
    catch { toast.error('Could not load generation config'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: cfg.provider, model: cfg.model,
        monthly_allowance: Number(cfg.monthly_allowance),
        credits_per_pack: Number(cfg.credits_per_pack),
        credit_price_cents: Number(cfg.credit_price_cents),
        est_cost_cents_per_gen: Number(cfg.est_cost_cents_per_gen),
        manual_review: !!cfg.manual_review,
        vision_moderation_enabled: !!cfg.vision_moderation_enabled,
        moderation_provider: cfg.moderation_provider,
        moderation_model: cfg.moderation_model,
      };
      const r = await api.put('/avatar-studio/admin/config', payload);
      setCfg(r.data.effective);
      toast.success('Generation settings saved');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  return (
    <Card data-testid="studio-config-card"><CardContent className="p-5 space-y-4">
      <p className="text-sm text-slate-500">Global defaults for the client Content Studio. Applied instantly; no code changes needed.</p>
      <div className="grid grid-cols-2 gap-3">
        <L label="Image provider"><Input value={cfg.provider || ''} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} data-testid="cfg-provider" /></L>
        <L label="Model"><Input value={cfg.model || ''} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} data-testid="cfg-model" /></L>
        {CFG_FIELDS.map((f) => (
          <L key={f.key} label={f.label}>
            <Input type="number" value={cfg[f.key] ?? 0} onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })} data-testid={`cfg-${f.key}`} />
          </L>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!cfg.manual_review} onChange={(e) => setCfg({ ...cfg, manual_review: e.target.checked })} data-testid="cfg-manual-review" />
        Hold all generations for manual review
      </label>
      <div className="border-t border-slate-100 pt-3 space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.vision_moderation_enabled} onChange={(e) => setCfg({ ...cfg, vision_moderation_enabled: e.target.checked })} data-testid="cfg-vision-enabled" />
          AI vision moderation of generated images before release
        </label>
        <div className="grid grid-cols-2 gap-3">
          <L label="Moderation provider"><Input value={cfg.moderation_provider || ''} onChange={(e) => setCfg({ ...cfg, moderation_provider: e.target.value })} data-testid="cfg-mod-provider" /></L>
          <L label="Moderation model"><Input value={cfg.moderation_model || ''} onChange={(e) => setCfg({ ...cfg, moderation_model: e.target.value })} data-testid="cfg-mod-model" /></L>
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="cfg-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save settings
      </Button>
    </CardContent></Card>
  );
}

function ModerationTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/avatar-studio/admin/moderation/queue?include_test=true'); setQueue(r.data.queue); }
    catch { toast.error('Could not load moderation queue'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/avatar-studio/admin/moderation/${id}/decide`, { action });
      toast.success(`Generation ${action}d`);
      setQueue((q) => q.filter((x) => x.id !== id));
    } catch { toast.error('Action failed'); } finally { setBusy(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  if (!queue.length) return <Card><CardContent className="p-12 text-center text-slate-400" data-testid="moderation-empty">No content awaiting review.</CardContent></Card>;
  return (
    <div className="space-y-3" data-testid="moderation-queue">
      {queue.map((g) => (
        <Card key={g.id} data-testid={`moderation-item-${g.id}`}><CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2"><Badge className="bg-amber-100 text-amber-700">Pending review</Badge>
              <span className="text-sm font-medium">{g.avatar_name}</span></div>
            <p className="text-xs text-rose-600 mt-1">{g.reason}</p>
            <p className="text-xs text-slate-500 mt-1">{g.client_email} · {Object.entries(g.controls || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || 'no styling'}</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === g.id} onClick={() => decide(g.id, 'approve')} data-testid={`mod-approve-${g.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" className="text-rose-600" disabled={busy === g.id} onClick={() => decide(g.id, 'reject')} data-testid={`mod-reject-${g.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

function UsageTab() {
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get('/avatar-studio/admin/usage?include_test=true'); setU(r.data); }
      catch { toast.error('Could not load usage'); } finally { setLoading(false); }
    })();
  }, []);
  if (loading || !u) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>;
  const stat = (label, value, color) => (
    <Card><CardContent className="p-4"><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color || 'text-slate-900'}`}>{value}</div></CardContent></Card>
  );
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="usage-report">
      {stat('Delivered', u.delivered)}
      {stat('Pending review', u.pending_review, 'text-amber-600')}
      {stat('Rejected', u.rejected, 'text-rose-600')}
      {stat('Failed (no charge)', u.failed, 'text-slate-500')}
      {stat('Provider cost', money(u.provider_cost_usd), 'text-rose-600')}
      {stat('Credit revenue', money(u.credit_revenue_usd), 'text-emerald-600')}
      {stat('Est. margin', money(u.estimated_margin_usd), u.estimated_margin_usd >= 0 ? 'text-emerald-600' : 'text-rose-600')}
      {stat('Credits sold', u.credits_sold)}
    </div>
  );
}
